/**
 * ProjectSelector.jsx
 * 
 * A dropdown menu to select a project from a list of available projects. It supports 
 * loading state, no projects available state, and selection functionality.
 *
 * @component
 */

import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

/**
 * ProjectSelector
 *
 * A dropdown that allows the user to select a project from a list. Displays loading
 * and no projects available states as necessary.
 *
 * @param {Object} props - The props for the component.
 * @param {boolean} props.disabled - Whether the dropdown should be disabled.
 * @param {Function} props.onChangeProject - Callback to handle when a project is selected.
 * @param {Array} props.projectLookup - Array of available projects.
 * @param {string} props.selectedProjectId - The ID of the currently selected project.
 *
 * @returns {JSX.Element} The rendered component.
 */
const ProjectSelector = ({
  disabled = false,
  onChangeProject,
  projectLookup,
  selectedProjectId = null,
}) => {
  const isSelectable = projectLookup?.length > 0;
  const isDisabled = disabled || !isSelectable;

  const LABEL_ID = "project-selector-label-id";
  const STATUS_VALUE = "status";

  return (
    <FormControl disabled={isDisabled} fullWidth size="small" aria-labelledby={LABEL_ID}>
      <InputLabel id={LABEL_ID} sx={{ top: "unset", left: "unset" }}>
        <FormattedMessage id="projectSelector.selectProject" />
      </InputLabel>
      <Select
        aria-live="polite"
        disabled={isDisabled}
        label={<FormattedMessage id="projectSelector.selectProject" />}
        labelId={LABEL_ID}
        onChange={e => onChangeProject(e.target.value)}
        value={
          isSelectable
            ? selectedProjectId
            : STATUS_VALUE
        }
      >
        {!projectLookup && !disabled &&
          <MenuItem value={STATUS_VALUE}>
            <CircularProgress
              aria-live="assertive"
              color="inherit"
              size="1em"
              sx={{ verticalAlign: "middle" }}
            />
          </MenuItem>
        }
        {(projectLookup && !isSelectable) &&
          <MenuItem value={STATUS_VALUE} aria-live="polite">
            <FormattedMessage id="projectSelector.noProjects" />
          </MenuItem>
        }
        {isSelectable && projectLookup.map(item => (
          <MenuItem
            role="option"
            aria-selected={item.projectId === selectedProjectId}
            key={item.projectId}
            value={item.projectId}
          >
            {item.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

ProjectSelector.propTypes = {
  disabled: PropTypes.bool,
  onChangeProject: PropTypes.func.isRequired,
  projectLookup: PropTypes.arrayOf(
    PropTypes.shape({
      projectId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedProjectId: PropTypes.string,
};

ProjectSelector.displayName = "ProjectSelector";

export default ProjectSelector;
